import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ICancellationPolicyModalResultsDisplayData } from 'model/view-models/results/product-card/price-display-data.interface';

@Component({
  selector: 'usb-cancellation-policies',
  templateUrl: './cancellation-policies.component.html',
  styleUrls: ['./cancellation-policies.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CancellationPoliciesComponent implements OnInit {

  @Input() cancellationPolicies!: ICancellationPolicyModalResultsDisplayData;
  @Input() currencies: any;
  @Input() currencyAgency: any;
  @Input() categoryId: number = 0;
  @Input() rentacarLayout: boolean = false;

    // ✅ Cachear los valores calculados
  private cachedValues: {
    segmentWidths: number[];
    todayPosition: number;
    nextDates: Date[];
    today: Date;
  } | null = null;

  private lastInputHash: string = '';


  constructor() { }

  ngOnInit(): void {
    this.calculateValues();
  }

adjustDate(date: string | Date, days: number): Date | null {
  if (!date) return null;

  const d = typeof date === 'string'
    ? new Date(date.includes('T') ? date : `${date}T00:00:00`)
    : new Date(date.getTime());

  if (isNaN(d.getTime())) return null;

  d.setDate(d.getDate() + days);
  return d;
}

  firstDayOfPreviousMonth(date: string | Date): Date | null {
    if (!date) return null;

    const d = typeof date === 'string'
      ? new Date(date.includes('T') ? date : `${date}T00:00:00`)
      : new Date(date.getTime());

    if (isNaN(d.getTime())) return null;

    return new Date(d.getFullYear(), d.getMonth() - 1, 1);
  }

splitHour(hour: any): string {
  if (!hour) {
    return '';
  }
  // Separa por espacio y toma la primera parte (ej: "13:00")
  return hour.split(' ')[0];
}

splitText(hour: any): string {
  if (!hour) {
    return '';
  }
  // Separa por espacio y toma el resto (ej: "UTC-3 Hora Argentina")
  const parts = hour.split(' ');
  parts.shift(); // Elimina la primera parte (la hora)
  return parts.join(' ');
}
  // ✅ Calcular todos los valores una sola vez
  private calculateValues(): void {
    if (!this.cancellationPolicies) return;

    const total = (this.cancellationPolicies?.cancellationPolicies?.length || 0) + 2;
    const segmentWidth = 100 / total;

    const today = new Date();
    const from = new Date(this.cancellationPolicies.from);
    const arrival = new Date(this.cancellationPolicies.arrivalDate);

    // Calcular posición del HOY
    let todayPosition = segmentWidth / 2;
    if (today >= from && today < arrival) {
      const policies = this.cancellationPolicies.cancellationPolicies;
      for (let i = 0; i < policies.length; i++) {
        const policyFrom = new Date(policies[i].from);
        const nextFrom = i < policies.length - 1 ? new Date(policies[i + 1].from) : arrival;
        if (today >= policyFrom && today < nextFrom) {
          todayPosition = ((i + 1) * segmentWidth) + segmentWidth / 2;
          break;
        }
      }
    } else if (today >= arrival) {
      todayPosition = 100 - segmentWidth / 2;
    }

    // Calcular fechas siguientes
    const nextDates: Date[] = [];
    const policies = this.cancellationPolicies.cancellationPolicies;
    for (let i = 0; i < policies.length; i++) {
      if (i < policies.length - 1) {
        nextDates.push(new Date(policies[i + 1].from));
      } else {
        nextDates.push(new Date(this.cancellationPolicies.arrivalDate));
      }
    }

    this.cachedValues = {
      segmentWidths: Array.from({ length: total }, () => segmentWidth),
      todayPosition,
      nextDates,
      today
    };
  }

  // ✅ Usar los valores cacheados
  // getSegmentWidth(index: number): number {
  //   if (!this.cachedValues) this.calculateValues();
  //   return this.cachedValues?.segmentWidths[index] || 0;
  // }

  getTodayPosition(): number {
    if (!this.cachedValues) this.calculateValues();
    return this.cachedValues?.todayPosition || 0;
  }

  getNextDate(index: number): Date {
    if (!this.cachedValues) this.calculateValues();
    return this.cachedValues?.nextDates[index] || new Date();
  }

  get today(): Date {
    if (!this.cachedValues) this.calculateValues();
    return this.cachedValues?.today || new Date();
  }

  // ✅ Forzar recálculo cuando cambian los inputs
  ngOnChanges(): void {
    this.calculateValues();
  }

  // En el componente
getActiveCancellationPolicies(): any[] {
  if (!this.cancellationPolicies?.cancellationPolicies) {
    return [];
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // ✅ Filtrar políticas que sean posteriores a hoy
  return this.cancellationPolicies.cancellationPolicies.filter(policy => {
    const policyDate = new Date(policy.from);
    policyDate.setHours(0, 0, 0, 0);
    return policyDate >= today;
  });
}

// ✅ Verificar si la política de cancelación gratuita está activa
isFreeCancellationActive(): boolean {
  if (!this.cancellationPolicies?.from) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const fromDate = new Date(this.cancellationPolicies.from);
  fromDate.setHours(0, 0, 0, 0);
  return fromDate >= today;
}

// ✅ Verificar si la política sin reembolso está activa
isNoRefundActive(): boolean {
  if (!this.cancellationPolicies?.arrivalDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const arrivalDate = new Date(this.cancellationPolicies.arrivalDate);
  arrivalDate.setHours(0, 0, 0, 0);
  return arrivalDate >= today;
}

getActivePoliciesCount(): number {
  let count = 0;
  if (this.isFreeCancellationActive()) count++;
  count += this.getActiveCancellationPolicies().length;
  if (this.isNoRefundActive()) count++;
  return count;
}

// ✅ Rango único para las políticas con penalidad parcial.
// Devuelve la policy con la fecha "from" más baja y la fecha final máxima
// junto con la penalidad más alta entre todas, para renderizar un único bloque.
getPartialPenaltyRange(): {
  firstPolicy: any;
  lastDate: Date | null;
  highestPenalty: any;
} | null {
  const policies = this.cancellationPolicies?.cancellationPolicies || [];
  const valid = policies.filter(p => p?.amount > 0);
  if (valid.length === 0) return null;

  const firstPolicy = valid.reduce((earliest, current) =>
    new Date(current.from) < new Date(earliest.from) ? current : earliest
  );

  const lastFromDate = valid.reduce((latest, current) =>
    new Date(current.from) > new Date(latest.from) ? current : latest
  );

  const arrival = this.cancellationPolicies?.arrivalDate
    ? this.adjustDate(this.cancellationPolicies.arrivalDate, -1)
    : null;

  const lastDate = arrival && new Date(lastFromDate.from) < arrival ? arrival : new Date(lastFromDate.from);

  const highestPenalty = valid.reduce((highest, current) => {
    const currentAmount = current.amountProf ?? current.amount ?? 0;
    const highestAmount = highest.amountProf ?? highest.amount ?? 0;
    return currentAmount > highestAmount ? current : highest;
  });

  return { firstPolicy, lastDate, highestPenalty };
}

getSegmentWidth(index: number): number {
  const total = this.getActivePoliciesCount() || 1;
  return 33;
}



}
